import * as React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import SortableTableHead, { Order } from '../Utilities/SortableTableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import { ResponseType, TokenRefreshContext } from '../Contexts/TokenRefreshContext';
import authService from '../api-authorization/AuthorizeService';
import CustomReportViewModel from './ViewModels/CustomReportViewModel';
import { Permission } from '../Permissions/Permissions';
import PermissionCheck from '../Utilities/PermissionCheck';
import CustomColumnsViewModel from './ViewModels/CustomColumnsViewModel';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import ReportCredibleDamageViewModel from './ViewModels/ReportCredibleDamageViewModel';
import ReportComponentViewModel from './ViewModels/ReportComponentViewModel';
import ReportSafetyDeviceViewModel from './ViewModels/ReportSafetyDeviceViewModel';
import { AllHeadCells, CompHeadCells, SafetyHeadCells, DamageHeadCells, ToggleHeadCell } from './ViewModels/ReportDefinitions';
import CustomReportRow from './CustomReportRow';
import DropdownViewModel from '../Utilities/ViewModels/DropdownViewModel';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/en-gb';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function CustomReport() {
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [client, setClient] = React.useState(0);
    const [plants, setPlants] = React.useState<DropdownViewModel[]>([]);
    const [startDate, setStartDate] = React.useState<Date | null | undefined>(null);
    const [endDate, setEndDate] = React.useState<Date | null | undefined>(null);
    const [clientList, setClientList] = React.useState<DropdownViewModel[]>([]);
    const [plantsList, setPlantsList] = React.useState<DropdownViewModel[]>([]);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [count, setCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof CustomReportViewModel>('wseNumber');
    const [results, setResults] = React.useState<CustomReportViewModel[]>([]);
    const [columnOptions, setColumnOptions] = React.useState<CustomColumnsViewModel>(new CustomColumnsViewModel());
    const [headCells, setHeadCells] = React.useState<ToggleHeadCell<CustomReportViewModel>[]>(AllHeadCells);
    const [compHeadCells, setCompHeadCells] = React.useState<ToggleHeadCell<ReportComponentViewModel>[]>(CompHeadCells);
    const [damageHeadCells, setDamageHeadCells] = React.useState<ToggleHeadCell<ReportCredibleDamageViewModel>[]>(DamageHeadCells);
    const [safetyHeadCells, setSafetyHeadCells] = React.useState<ToggleHeadCell<ReportSafetyDeviceViewModel>[]>(SafetyHeadCells);

    React.useEffect(() => {
        getDropdowns();
    }, []);

    React.useEffect(() => {
        getPlantDropdown();
    }, [client]);

    React.useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        getData(signal);

        return () => {
            controller.abort();
        }
    }, [page, rowsPerPage, order, orderBy]);

    const getDropdowns = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Client/GetClientsDropDown`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: DropdownViewModel[]) => {
                const newDropdownItem = new DropdownViewModel();
                newDropdownItem.id = 0;
                newDropdownItem.label = 'All Clients';
                data.unshift(newDropdownItem);
                setClientList(data);
            }
        );
    }

    const getPlantDropdown = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Plant/GetPlantsDropdown?client=${client > 0 ? client : ''}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setPlantsList(data);
            }
        );
    }

    const getData = async (signal?: AbortSignal) => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`Report/GetCustomReport?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&client=${client > 0 ? client : ''}&start=${startDate ? moment(startDate).add(1, 'h').toISOString() : ''}&end=${endDate ? moment(endDate).add(1, 'h').toISOString() : ''}`, {
            signal: signal,
            method: 'POST',
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify(plants.map(m => m.id))
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const getCsv = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`Report/GetCustomReportCsv?orderBy=${orderByProp}&order=${order}&client=${client > 0 ? client : ''}&start=${startDate ? moment(startDate).add(1, 'h').toISOString() : ''}&end=${endDate ? moment(endDate).add(1, 'h').toISOString() : ''}`, {
            method: 'POST',
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify({
                columns: columnOptions,
                plants: plants.map(m => m.id)
            })
        }, ResponseType.Blob,
            (blob: Blob) => {
                let dateTime = new Date();
                const formattedTime = dateTime.toLocaleDateString() + "_" + dateTime.toLocaleTimeString(navigator.language, {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                var url = window.URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.href = url;
                a.download = "CustomReport_Export_" + formattedTime + ".csv";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const updateColumns = async () => {
        searchData();
    }

    const handleColumnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setColumnOptions({
            ...columnOptions,
            [event.target.name]: event.target.checked,
        });

        const prefix = event.target.name.split(/(?=[A-Z])(.*)/);
        const name = prefix[1];

        switch (prefix[0]) {
            case "component":
                const compheaders = compHeadCells.map(item => {
                    if (item.id.toLowerCase() === name.toLowerCase())
                        return { ...item, enabled: event.target.checked };
                    else
                        return item;
                })
                setCompHeadCells(compheaders);
                break;
            case "safety":
                const safetyheaders = safetyHeadCells.map(item => {
                    if (item.id.toLowerCase() === name.toLowerCase())
                        return { ...item, enabled: event.target.checked };
                    else
                        return item;
                })
                setSafetyHeadCells(safetyheaders);
                break;
            case "damage":
                const damageheaders = damageHeadCells.map(item => {
                    if (item.id.toLowerCase() === name.toLowerCase())
                        return { ...item, enabled: event.target.checked };
                    else
                        return item;
                })
                setDamageHeadCells(damageheaders);
                break;
            default:
                const headers = headCells.map(item => {
                    if (item.id.toLowerCase() === event.target.name.toLowerCase())
                        return { ...item, enabled: event.target.checked };
                    else
                        return item;
                })
                setHeadCells(headers);
                break;
        }
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof CustomReportViewModel) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const searchData = () => {
        if (page === 0) {
            getData();
        }

        setPage(0);
    }

    const setClientInput = (input: string) => {
        const idString = clientList.find(c => c.label === input)?.id;
        const parsedId = parseInt(idString as string ?? '0', 10);

        if (!isNaN(parsedId)) {
            setClient(parsedId);
        }
    }

    const setPlantInput = (input: string) => {
        const idString = plantsList.find(c => c.label === input)?.id;
        const parsedId = parseInt(idString as string ?? '0', 10);

        if (!isNaN(parsedId)) {
            if (plants.findIndex(f => f.id === parsedId) === -1) {
                const plant = plantsList.find(f => f.id === parsedId);

                if (plant) {
                    const newPlants = [...plants];
                    newPlants.push(plant);
                    setPlants(newPlants);
                }
            }
        }
    }

    const showDetails = React.useMemo(() => {
        return compHeadCells.findIndex(f => f.enabled === true) > -1 ||
            damageHeadCells.findIndex(f => f.enabled === true) > -1 ||
            safetyHeadCells.findIndex(f => f.enabled === true) > -1;
    }, [compHeadCells, safetyHeadCells, damageHeadCells]);

    const emptyRows = Math.max(0, rowsPerPage - (results ? results.length : 0));

    const clientFilterValue = React.useMemo(() => {
        return clientList.find(f => f.id === client);
    }, [client, clientList]);

    const plantFilterValue = React.useMemo(() => {
        return plantsList.filter(f => plants.includes(f));
    }, [plants, plantsList]);

    return (
        <PermissionCheck permission={Permission.ViewClients}>
            <Grid container spacing={1}>
                <Grid item>
                    <Typography variant="h1">WSE Reporting</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Accordion defaultExpanded sx={{ padding: 0 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h2">Selected Columns for Report</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Design Information</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="client" checked={columnOptions.client} />} label="Client" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="plant" checked={columnOptions.plant} />} label="Plant" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="site" checked={columnOptions.site} />} label="Site" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="assetType" checked={columnOptions.assetType} />} label="Equipment Type" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="dateOfFabrication" checked={columnOptions.dateOfFabrication} />} label="Commissioning Date" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="designCode" checked={columnOptions.designCode} />} label="Design Code" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="description" checked={columnOptions.description} />} label="Description" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="plantItemNumber" checked={columnOptions.plantItemNumber} />} label="Registered No." />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="serialNumber" checked={columnOptions.serialNumber} />} label="Serial No." />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="service" checked={columnOptions.service} />} label="Service" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="pids" checked={columnOptions.pids} />} label="P&ID's / Drawings" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="pssr" checked={columnOptions.pssr} />} label="PSSR" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Components</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentName" checked={columnOptions.componentName} />} label="Name" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentMaterial" checked={columnOptions.componentMaterial} />} label="Material" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentNominalThickness" checked={columnOptions.componentNominalThickness} />} label="Nominal Thickness" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentDesignPressure" checked={columnOptions.componentDesignPressure} />} label="Design Pressure" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentDesignTemperatureRange" checked={columnOptions.componentDesignTemperatureRange} />} label="Design Temperature" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentFluidService" checked={columnOptions.componentFluidService} />} label="Fluid Service" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentOperatingPressure" checked={columnOptions.componentOperatingPressure} />} label="Operating Pressure" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentOperatingTemperature" checked={columnOptions.componentOperatingTemperature} />} label="Operating Temperature" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentCorrosionAllowance" checked={columnOptions.componentCorrosionAllowance} />} label="Corrosion Allowance" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Relief System Components</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentManufacturer" checked={columnOptions.componentManufacturer} />} label="Manufacturer" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentModel" checked={columnOptions.componentModel} />} label="Model" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentSerialNumber" checked={columnOptions.componentSerialNumber} />} label="Serial Number" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentConventionalBellowsPilot" checked={columnOptions.componentConventionalBellowsPilot} />} label="Conventional/Bellows/Pilot" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentSetPressure" checked={columnOptions.componentSetPressure} />} label="Set Pressure" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentReliefFrom" checked={columnOptions.componentReliefFrom} />} label="Relief From" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentReliefTo" checked={columnOptions.componentReliefTo} />} label="Relief To" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentEquipmentProtected" checked={columnOptions.componentEquipmentProtected} />} label="Equipment Protected" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Pressure Vessel Components</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="componentOtherComments" checked={columnOptions.componentOtherComments} />} label="Other Comments" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item xs={12}>
                                        <Typography variant="h3">Safety Devices</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="safetyEquipmentNumber" checked={columnOptions.safetyEquipmentNumber} />} label="Equipment Number" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="safetyType" checked={columnOptions.safetyType} />} label="Type" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="safetyColdDifferential" checked={columnOptions.safetyColdDifferential} />} label="Cold Differential Set Pressure" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="safetyReliefSystemWse" checked={columnOptions.safetyReliefSystemWse} />} label="Relief System WSE No." />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Damage Mechanisms</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="damageName" checked={columnOptions.damageName} />} label="Name" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="damageLocation" checked={columnOptions.damageLocation} />} label="Location" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="damagePreparation" checked={columnOptions.damagePreparation} />} label="Preparation" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="damageInspectionRequirement" checked={columnOptions.damageInspectionRequirement} />} label="Inspection Requirement" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="column">
                                    <Grid item>
                                        <Typography variant="h3">Inspections</Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="thoroughInterval" checked={columnOptions.thoroughInterval} />} label="Thorough Examination Interval" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="thoroughNotes" checked={columnOptions.thoroughNotes} />} label="Thorough Examination Notes" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="intermediateAReqs" checked={columnOptions.intermediateAReqs} />} label="Intermediate A Requirements" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="intermediateAInterval" checked={columnOptions.intermediateAInterval} />} label="Intermediate A Interval" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="intermediateBReqs" checked={columnOptions.intermediateBReqs} />} label="Intermediate B Requirements" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="intermediateBInterval" checked={columnOptions.intermediateBInterval} />} label="Intermediate B Interval" />
                                            <FormControlLabel control={<Checkbox size="small" onChange={handleColumnChange} name="oneOffReqs" checked={columnOptions.oneOffReqs} />} label="One Off Inspection Requirements" />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid item xs={12}>
                <Paper>
                    <Grid container spacing={1} justifyContent="space-between">
                        <Grid item>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    {clientList.length > 0 &&
                                        <Autocomplete
                                            disablePortal
                                            size="small"
                                            fullWidth
                                            getOptionKey={(option) => option.id}
                                            options={clientList}
                                            sx={{ width: 250 }}
                                            onInputChange={(value, newInputValue) => setClientInput(newInputValue)}
                                            value={clientFilterValue}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    }
                                </Grid>
                                <Grid item>
                                    {plantsList.length > 0 &&
                                        <Autocomplete
                                            multiple
                                            disablePortal
                                            size="small"
                                            fullWidth
                                            disableCloseOnSelect
                                            options={plantsList}
                                            sx={{ minWidth: 500 }}
                                            getOptionKey={(option) => option.id}
                                            getOptionLabel={(option) => option.label as string}
                                            onInputChange={(value, newInputValue) => setPlantInput(newInputValue)}
                                            onChange={(event, newValues) => setPlants(newValues)}
                                            value={plantFilterValue}
                                            renderOption={(props, option, { selected }) => {
                                                const { ...optionProps } = props;
                                                return (
                                                    <li key={'plantOption' + option.id} {...optionProps}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={selected}
                                                        />
                                                        {option.label}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <Button variant="contained" type="button" onClick={updateColumns} disabled={loading}>Apply & Search</Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="contained" type="button" onClick={getCsv} disabled={loading}>Export CSV</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center">
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="enGB">
                                    <Grid item>
                                        <Typography variant="h6">From First Action Date</Typography>
                                    </Grid>
                                    <Grid item>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: { actions: ['clear'] },
                                            }}
                                            onChange={(newVal => setStartDate(newVal))}
                                            value={startDate}
                                            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="h6">to Last Action Date</Typography>
                                    </Grid>
                                    <Grid item>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: { actions: ['clear'] },
                                            }}
                                            onChange={(newVal => setEndDate(newVal))}
                                            value={endDate}
                                            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                        />
                                    </Grid>
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <TableContainer>
                                <ResponsiveTable
                                    aria-labelledby="tableTitle"
                                    size={'small'}
                                    aria-label="custom report table"
                                >
                                    <SortableTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        headCells={headCells.filter(f => f.enabled)}
                                        firstCells={showDetails ? <TableCell /> : undefined}
                                    />
                                    <TableBody>
                                        {(!loading && results) && results.map((row: CustomReportViewModel) => {
                                            return (<CustomReportRow key={row.id} row={row}
                                                headCells={headCells.filter(f => f.enabled)}
                                                compCells={compHeadCells.filter(f => f.enabled)}
                                                damageCells={damageHeadCells.filter(f => f.enabled)}
                                                safetyCells={safetyHeadCells.filter(f => f.enabled)} />
                                            );
                                        })}
                                        {count <= 0 && !loading &&
                                            <TableRow
                                                sx={{
                                                    height: 32 * rowsPerPage,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} align="center">No Results Found</TableCell>
                                            </TableRow>
                                        }
                                        {count > 0 && emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 32 * emptyRows,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} />
                                            </TableRow>
                                        )}
                                        {loading &&
                                            <TableRow
                                                sx={{
                                                    height: 32 * rowsPerPage,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </ResponsiveTable>
                            </TableContainer>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={count}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
        </PermissionCheck >
    );
}